import { chatDebug } from '../../utils/chatDebug';
import { normalizeChatUser } from '../../utils/interveneApiAdapters';
import { resolveBackgroundMessageSoundType } from '../../utils/interveneMessageSound';
import { userMatchesActiveTab } from '../../utils/userTabMembership';
import { isInterveneSessionActive } from '../../utils/sessionToggleRules';
import { playMessageSound } from '../../../../shared/notifications';
import { normalizeListPatchFromSocket } from './normalizeListPatch';

function normalizeBotMatch(currentBot, incomingBot) {
  if (!currentBot || !incomingBot) return true;
  const cleanCurrent = `${currentBot}`.replace(/\D/g, '');
  const cleanIncoming = `${incomingBot}`.replace(/\D/g, '');
  if (!cleanCurrent || !cleanIncoming) return true;
  return (
    cleanCurrent === cleanIncoming ||
    (cleanCurrent.length >= 10 &&
      cleanIncoming.length >= 10 &&
      cleanCurrent.slice(-10) === cleanIncoming.slice(-10))
  );
}

function sortUsersByRecency(users) {
  return [...users].sort((a, b) => {
    const aTime = new Date(a.updated_at || a.last_active_date || 0).getTime();
    const bTime = new Date(b.updated_at || b.last_active_date || 0).getTime();
    return bTime - aTime;
  });
}

function patchUserInList(users, patch) {
  const phone = patch?.phone_number;
  if (!phone) return users;

  let found = false;
  const next = users.map((user) => {
    if (`${user.phone_number}` !== `${phone}`) return user;
    found = true;
    return normalizeChatUser({ ...user, ...patch });
  });

  if (!found) {
    next.unshift(normalizeChatUser(patch));
  }

  return sortUsersByRecency(next);
}

function removeUserFromList(users, phoneNumber) {
  if (!phoneNumber) return users;
  return users.filter((user) => `${user.phone_number}` !== `${phoneNumber}`);
}

function isChatMessagePayload(payload) {
  return Boolean(
    payload?.content ||
    payload?.msg_id ||
    payload?.w_id ||
    payload?.text ||
    payload?.message
  );
}

function isListItemPayload(payload) {
  return Boolean(payload?.last_message_preview && payload?.phone_number);
}

function isExplicitSessionEndPayload(payload) {
  return Boolean(
    payload?.phone_number &&
    payload.intervene === false &&
    (payload.session_start === false || payload.session_start === null) &&
    !isChatMessagePayload(payload) &&
    !isListItemPayload(payload)
  );
}

function shouldPreserveLiveSession(current, payload, patch) {
  if (!isInterveneSessionActive(current)) return false;
  const patchClearsSession =
    patch?.intervene === false ||
    patch?.session_start === null ||
    patch?.session_start === false;
  if (!patchClearsSession) return false;
  if (isExplicitSessionEndPayload(payload)) return false;
  if (isChatMessagePayload(payload)) return true;
  if (
    isListItemPayload(payload) &&
    (payload.session_start === true ||
      (payload.session_start && payload.session_start !== false))
  ) {
    return true;
  }
  return false;
}

function applyPreservedSessionFields(target, source) {
  target.intervene = source.intervene;
  target.session_start = source.session_start;
  target.intervened_by = source.intervened_by ?? target.intervened_by;
  target.intervened_by_user_id = source.intervened_by_user_id ?? target.intervened_by_user_id;
  target.intervened_at = source.intervened_at ?? target.intervened_at;
  target.user_status = source.user_status || target.user_status;
}

function parseActivityTime(value) {
  if (!value) return null;
  const ts = new Date(value).getTime();
  return Number.isNaN(ts) ? null : ts;
}

/** list_item WS rows often carry stale last_active_date; keep the newer timestamp. */
function mergeLastActiveDate(currentValue, patchValue) {
  const currentTs = parseActivityTime(currentValue);
  const patchTs = parseActivityTime(patchValue);
  if (currentTs == null) return patchValue ?? currentValue;
  if (patchTs == null) return currentValue;
  return patchTs >= currentTs ? patchValue : currentValue;
}

/**
 * Apply a socket list patch for the active tab only.
 */
function applySocketListUpdate(prev, { patch, tabValue, existingUser, currentUserId }) {
  if (!patch || !tabValue) return prev;

  const users = prev.users || [];
  const mergedUser = existingUser
    ? normalizeChatUser({ ...existingUser, ...patch })
    : normalizeChatUser(patch);
  const belongsToActiveTab = userMatchesActiveTab(mergedUser, tabValue, new Date(), currentUserId);

  if (belongsToActiveTab) {
    const nextUsers = patchUserInList(users, patch);
    const wasNew = !existingUser;
    return {
      ...prev,
      users: nextUsers,
      total_count: wasNew ? (prev.total_count || 0) + 1 : prev.total_count,
    };
  }

  if (existingUser) {
    return {
      ...prev,
      users: removeUserFromList(users, patch.phone_number),
      total_count: Math.max(0, (prev.total_count || 0) - 1),
    };
  }

  return prev;
}

function applyListSocketUpdate({
  payload,
  getFilters,
  getActiveUser,
  getCurrentUserId,
  setUserList,
  setActiveUser,
}) {
  const filters = getFilters();
  if (
    filters?.botNumber &&
    payload?.bot_number &&
    !normalizeBotMatch(filters.botNumber, payload.bot_number)
  ) {
    return;
  }

  const activeUser = getActiveUser();
  const currentUserId = getCurrentUserId?.() ?? null;
  const isActiveChat =
    `${activeUser?.phone_number}` === `${payload.phone_number}`;

  const backgroundSoundType = resolveBackgroundMessageSoundType({
    payload,
    isActiveChat,
    existingUser: true,
    listChanged: true,
  });
  if (backgroundSoundType) {
    playMessageSound(backgroundSoundType);
  }

  if (isActiveChat && typeof setActiveUser === 'function') {
    const activePatch = normalizeListPatchFromSocket(payload, {
      existingUser: activeUser,
      isActiveChat: true,
    });
    if (activePatch) {
      setActiveUser((current) => {
        if (!current?.phone_number) return current;
        const preserveLiveSession = shouldPreserveLiveSession(current, payload, activePatch);
        const merged = {
          ...current,
          ...activePatch,
          intervene: activePatch.intervene !== undefined ? activePatch.intervene : current.intervene,
          session_start:
            activePatch.session_start !== undefined ? activePatch.session_start : current.session_start,
          intervened_by:
            activePatch.intervened_by !== undefined ? activePatch.intervened_by : current.intervened_by,
          intervened_by_user_id:
            activePatch.intervened_by_user_id !== undefined
              ? activePatch.intervened_by_user_id
              : current.intervened_by_user_id,
        };
        if (preserveLiveSession) {
          applyPreservedSessionFields(merged, current);
        }
        merged.last_active_date = mergeLastActiveDate(
          current.last_active_date,
          activePatch.last_active_date
        );
        merged.updated_at = mergeLastActiveDate(current.updated_at, activePatch.updated_at);
        return merged;
      });
    }
  }

  if (filters?.search) return;

  setUserList((prev) => {
    const listUser = (prev.users || []).find(
      (user) => `${user.phone_number}` === `${payload.phone_number}`
    );
    const existingUser =
      isActiveChat && activeUser?.phone_number ? activeUser : listUser;
    let patch = normalizeListPatchFromSocket(payload, {
      existingUser,
      isActiveChat,
    });

    const sessionSource =
      isActiveChat && activeUser?.phone_number ? activeUser : existingUser;
    if (patch && sessionSource && shouldPreserveLiveSession(sessionSource, payload, patch)) {
      patch = { ...patch };
      applyPreservedSessionFields(patch, sessionSource);
    }
    if (patch && sessionSource) {
      patch = { ...patch };
      patch.last_active_date = mergeLastActiveDate(
        sessionSource.last_active_date,
        patch.last_active_date
      );
      patch.updated_at = mergeLastActiveDate(sessionSource.updated_at, patch.updated_at);
    }

    if (
      filters?.botNumber &&
      patch?.bot_number &&
      !normalizeBotMatch(filters.botNumber, patch.bot_number)
    ) {
      return prev;
    }

    const next = applySocketListUpdate(prev, {
      patch,
      tabValue: filters?.userType,
      existingUser: listUser,
      currentUserId,
    });

    if (next !== prev) {
      chatDebug('list-socket-update', {
        phone: payload.phone_number,
        tab: filters?.userType,
        user_status: patch?.user_status,
        last_active_date: patch?.last_active_date,
        action: listUser ? 'patch' : 'insert',
        listSize: next.users?.length,
      });
    } else {
      chatDebug('list-socket-skip', {
        phone: payload.phone_number,
        tab: filters?.userType,
        user_status: patch?.user_status,
        last_active_date: patch?.last_active_date,
        inList: Boolean(listUser),
      });
    }

    return next;
  });
}

export function createHandleSessionUpdated({
  getActiveUser,
  getFilters,
  getCurrentUserId,
  setUserList,
  setActiveUser,
}) {
  return (sessionDoc) => {
    if (!sessionDoc?.phone_number) return;

    applyListSocketUpdate({
      payload: sessionDoc,
      getFilters,
      getActiveUser,
      getCurrentUserId,
      setUserList,
      setActiveUser,
    });
  };
}

export function createHandleListUpdated({
  getActiveUser,
  getFilters,
  getCurrentUserId,
  setUserList,
  setActiveUser,
}) {
  return (payload) => {
    if (!payload?.phone_number) return;

    applyListSocketUpdate({
      payload,
      getFilters,
      getActiveUser,
      getCurrentUserId,
      setUserList,
      setActiveUser,
    });
  };
}

export { applySocketListUpdate, patchUserInList, removeUserFromList };
